/* eslint-disable no-magic-numbers */
export function isPicture(file: File): boolean {
	return file && (file.type === "image/jpeg" || file.type === "image/pjpeg" || file.type === "image/png");
}

export function getImageDim(img: HTMLImageElement, maxSize: number = 0) {
	const output = {
		width: img.width,
		height: img.height,
		scale: 1,
	};
	const largeWidth = maxSize > 0 && img.width > maxSize;
	const largeHeight = maxSize > 0 && img.height > maxSize;

	if (largeWidth || largeHeight) {
		// resize picture
		let imgWidth = img.width;
		let imgHeight = img.height;

		// portrait x landscape
		if (img.width > img.height) {
			// landscape
			imgHeight = maxSize * imgHeight / imgWidth;
			imgWidth = maxSize;
		} else {
			// portrait
			imgWidth = maxSize * imgWidth / imgHeight;
			imgHeight = maxSize;
		}

		// ratio between original x scaled image
		output.scale = img.width / imgWidth;
		output.width = imgWidth;
		output.height = imgHeight;
	}

	return output;
}

export function getCanvas(imgData: any, optsArg: { [key: string]: string|number|boolean }) {
	const opts = {
		width: imgData.width || 0,
		height: imgData.height || 0,
		orientation: 0,
		scaled: false,
		canvas: null,
		...optsArg,
	};

	const canvas = opts.canvas || document.createElement("canvas");

	canvas.width = opts.width;
	canvas.height = opts.height;

	const ctx = canvas.getContext("2d");

	if (ctx) {
		let draw = true;

		// rotate
		if (opts.orientation) {
			switch (opts.orientation) {
				case 2:
					// horizontal flip
					ctx.translate(opts.width, 0);
					ctx.scale(-1, 1);
					break;

				case 180:
				case 3:
					// 180° rotate left
					ctx.translate(opts.width, opts.height);
					ctx.rotate(Math.PI);
					break;

				case 4:
					// vertical flip
					ctx.translate(0, opts.height);
					ctx.scale(1, -1);
					break;

				case 5:
					// vertical flip + 90 rotate right
					canvas.width = opts.height;
					canvas.height = opts.width;
					ctx.rotate(0.5 * Math.PI);
					ctx.scale(1, -1);

					if (opts.scaled) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(imgData, 0, 0, imgData.width, imgData.height, 0, 0, canvas.height, canvas.width);

						draw = false;
					}
					break;

				case 90:
				case 6:
					// 90° rotate right
					canvas.width = opts.height;
					canvas.height = opts.width;
					ctx.rotate(0.5 * Math.PI);
					ctx.translate(0, -opts.height);

					if (opts.scaled) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(imgData, 0, 0, imgData.width, imgData.height, 0, 0, canvas.height, canvas.width);

						draw = false;
					}
					break;

				case 7:
					// horizontal flip + 90 rotate right
					canvas.width = opts.height;
					canvas.height = opts.width;
					ctx.rotate(0.5 * Math.PI);
					ctx.translate(opts.width, -opts.height);
					ctx.scale(-1, 1);

					if (opts.scaled) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(imgData, 0, 0, imgData.width, imgData.height, 0, 0, canvas.height, canvas.width);

						draw = false;
					}
					break;

				case 270:
				case 8:
					// 90° rotate left
					canvas.width = opts.height;
					canvas.height = opts.width;
					ctx.rotate(-0.5 * Math.PI);
					ctx.translate(-opts.width, 0);

					if (opts.scaled) {
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						ctx.drawImage(imgData, 0, 0, imgData.width, imgData.height, 0, 0, canvas.height, canvas.width);

						draw = false;
					}
					break;
				default:
			}
		}

		if (draw) {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			if (opts.scaled) {
				ctx.drawImage(imgData, 0, 0, imgData.width, imgData.height, 0, 0, canvas.width, canvas.height);
			} else {
				ctx.drawImage(imgData, 0, 0);
			}
		}
	}

	return canvas;
}

export function imageFromFile(file: File, maxSize: number) {
	return new Promise((resolve, reject) => {
		const output: {
			img: null|HTMLImageElement;
			canvas: null|HTMLCanvasElement;
		} = {
			img: null,
			canvas: null,
		};

		const reader = new FileReader();

		reader.addEventListener("load", () => {
			const img = new Image();

			img.addEventListener("load", () => {
				const imd = getImageDim(img, maxSize);
				const canvas = getCanvas(img, {
					width: imd.width,
					height: imd.height,
					scaled: imd.scale !== 1,
				});

				output.img = img;
				output.canvas = canvas;

				resolve(output);
			});
			img.addEventListener("error", error => {
				reject(error);
			});
			img.src = reader.result as string;
		});
		reader.addEventListener("error", error => {
			reject(error);
		});
		reader.readAsDataURL(file);
	});
}
